/**
 * Route Price Form Page Component
 * /admin/route-prices/new and /admin/route-prices/:id/edit
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiClient from '../api/client';

const RoutePriceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [priceLists, setPriceLists] = useState([]);
  const [locations, setLocations] = useState([]);
  // From the server's field metadata, not a second copy kept here.
  const [departments, setDepartments] = useState([]);

  const [formData, setFormData] = useState({
    price_list: '',
    pickup_location_id: '',
    dropoff_location_id: '',
    base_price: '',
    department: '',
    cost_center_code: '',
    priority: 0,
    min_distance: '',
    max_distance: '',
    notes: '',
    is_active: true,
  });

  useEffect(() => {
    loadPriceLists();
    loadLocations();
    loadDepartments();
    if (isEdit) {
      loadRoutePrice();
    }
  }, [id]);

  const loadDepartments = async () => {
    try {
      setDepartments(
        await apiClient.getFieldChoices('/bookings/route-prices/', 'department')
      );
    } catch (err) {
      // Optional field; an unreachable metadata endpoint should not block the
      // rest of the form.
      console.error('Failed to load department choices:', err);
      setDepartments([]);
    }
  };

  const loadPriceLists = async () => {
    try {
      const response = await apiClient.getCorporatePriceLists({ is_active: true });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setPriceLists(data);
    } catch (err) {
      console.error('Failed to load price lists:', err);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await apiClient.getLocations({ is_active: true });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadRoutePrice = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getRoutePrice(id);
      
      setFormData({
        price_list: data.price_list || '',
        pickup_location_id: data.pickup_location?.id || '',
        dropoff_location_id: data.dropoff_location?.id || '',
        base_price: data.base_price || '',
        department: data.department || '',
        cost_center_code: data.cost_center_code || '',
        priority: data.priority ?? 0,
        min_distance: data.min_distance || '',
        max_distance: data.max_distance || '',
        notes: data.notes || '',
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
    } catch (err) {
      setError(err.message || 'Failed to load route price');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const submitData = {
        price_list: formData.price_list,
        pickup_location_id: formData.pickup_location_id,
        dropoff_location_id: formData.dropoff_location_id,
        base_price: parseFloat(formData.base_price),
        is_active: formData.is_active,
      };

      if (formData.department) submitData.department = formData.department;
      if (formData.cost_center_code) submitData.cost_center_code = formData.cost_center_code;
      // Sent whenever it is a number, including 0: `if (formData.priority)`
      // dropped the model's default and left the field unchanged on edit.
      if (formData.priority !== '' && formData.priority !== null) {
        submitData.priority = Number(formData.priority);
      }
      if (formData.min_distance) submitData.min_distance = parseFloat(formData.min_distance);
      if (formData.max_distance) submitData.max_distance = parseFloat(formData.max_distance);
      if (formData.notes) submitData.notes = formData.notes;

      let response;
      if (isEdit) {
        response = await apiClient.updateRoutePrice(id, submitData);
        setSuccessMessage('Route price updated successfully!');
      } else {
        response = await apiClient.createRoutePrice(submitData);
        setSuccessMessage('Route price created successfully!');
      }

      // Navigate to route price detail page after a short delay
      setTimeout(() => {
        navigate(`/admin/route-prices/${response.id || id}`);
      }, 1500);
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} route price`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/route-prices')}
            sx={{ mb: 1 }}
          >
            Back to Route Prices
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Route Price' : 'New Route Price'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Price List */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Price List</InputLabel>
                <Select
                  name="price_list"
                  value={formData.price_list}
                  onChange={handleChange}
                  label="Price List"
                >
                  <MenuItem value="">Select Price List</MenuItem>
                  {priceLists.map((priceList) => (
                    <MenuItem key={priceList.id} value={priceList.id}>
                      {priceList.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Pickup Location */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name || ''}
                value={locations.find(l => l.id === formData.pickup_location_id) || null}
                onChange={(e, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    pickup_location_id: newValue ? newValue.id : '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pickup Location"
                    required
                  />
                )}
              />
            </Grid>

            {/* Dropoff Location */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name || ''}
                value={locations.find(l => l.id === formData.dropoff_location_id) || null}
                onChange={(e, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    dropoff_location_id: newValue ? newValue.id : '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Dropoff Location"
                    required
                  />
                )}
              />
            </Grid>

            {/* Base Price */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="base_price"
                label="Base Price (AED)"
                type="number"
                value={formData.base_price}
                onChange={handleChange}
                required
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
              />
            </Grid>

            {/* Priority. An integer, not a severity: the column decides which
                of several matching route prices is used, and the dropdown here
                offered low/normal/medium/high against an IntegerField, so every
                save answered "A valid integer is required." */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                name="priority"
                label="Match Priority"
                value={formData.priority}
                onChange={handleChange}
                slotProps={{ htmlInput: { step: 1, min: 0 } }}
                helperText="Higher wins when more than one route price matches"
              />
            </Grid>

            {/* Department. Free text against a column that accepts only
                DD/MWD/RNM/SDL/SPERRY-MWD/ALL, so anything else typed here was
                refused on save. The choices come from the server. */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  label="Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.display_name || choice.value}
                    </MenuItem>
                  ))}
                  {formData.department &&
                    !departments.some((c) => c.value === formData.department) && (
                      <MenuItem value={formData.department}>
                        {formData.department} (no longer offered)
                      </MenuItem>
                    )}
                </Select>
              </FormControl>
            </Grid>

            {/* Cost Center Code */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="cost_center_code"
                label="Cost Center Code"
                value={formData.cost_center_code}
                onChange={handleChange}
              />
            </Grid>

            {/* Distance Range */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="min_distance"
                label="Min Distance (km)"
                type="number"
                value={formData.min_distance}
                onChange={handleChange}
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="max_distance"
                label="Max Distance (km)"
                type="number"
                value={formData.max_distance}
                onChange={handleChange}
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
              />
            </Grid>

            {/* Notes */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                name="notes"
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>

            {/* Active Status */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Submit Button */}
            <Grid size={{ xs: 12 }}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/route-prices')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEdit ? 'Update Route Price' : 'Create Route Price'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default RoutePriceForm;

